/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/utils/permissions.ts
 * PURPOSE: Utility functions and helper methods
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (Code Reusability)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Utility Functions, Helper Libraries, Vietnamese F&B Utilities
 * - Impact: Code Reusability, Developer Productivity, Maintainability
 * - Stakeholders: Development Team, Frontend Engineers
 *
 * @technical-architecture
 * - Layer: Utility Layer, Helper Functions
 * - Pattern: Pure Functions, Utility Patterns, Type-Safe Helpers
 * - Dependencies: TypeScript, Lodash (optional)
 */

// User roles in hierarchical order (higher index = more permissions)
export enum UserRole {
  EMPLOYEE = 'employee',
  SUPERVISOR = 'supervisor', 
  MANAGER = 'manager',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin'  // Platform-level administrator (NQH)
}

// Available permissions/features
export enum Permission {
  // Attendance Management
  VIEW_OWN_ATTENDANCE = 'view_own_attendance',
  VIEW_TEAM_ATTENDANCE = 'view_team_attendance',
  VIEW_ALL_ATTENDANCE = 'view_all_attendance',
  EDIT_ATTENDANCE = 'edit_attendance',
  DELETE_ATTENDANCE = 'delete_attendance',

  // Schedule Management
  VIEW_OWN_SCHEDULE = 'view_own_schedule',
  VIEW_TEAM_SCHEDULE = 'view_team_schedule',
  VIEW_ALL_SCHEDULES = 'view_all_schedules',
  CREATE_SCHEDULE = 'create_schedule',
  EDIT_SCHEDULE = 'edit_schedule',
  DELETE_SCHEDULE = 'delete_schedule',
  APPROVE_SCHEDULE_CHANGES = 'approve_schedule_changes',

  // Task Management
  VIEW_OWN_TASKS = 'view_own_tasks',
  VIEW_TEAM_TASKS = 'view_team_tasks',
  VIEW_ALL_TASKS = 'view_all_tasks',
  CREATE_TASK = 'create_task',
  ASSIGN_TASK = 'assign_task',
  EDIT_TASK = 'edit_task',
  DELETE_TASK = 'delete_task',

  // User Management
  VIEW_TEAM_MEMBERS = 'view_team_members',
  VIEW_ALL_USERS = 'view_all_users',
  CREATE_USER = 'create_user',
  EDIT_USER = 'edit_user',
  DELETE_USER = 'delete_user',
  MANAGE_ROLES = 'manage_roles',

  // Organization Management
  VIEW_ORGANIZATION = 'view_organization',
  EDIT_ORGANIZATION = 'edit_organization',
  MANAGE_BRANCHES = 'manage_branches',
  MANAGE_DEPARTMENTS = 'manage_departments',

  // Reports & Analytics
  VIEW_OWN_REPORTS = 'view_own_reports',
  VIEW_TEAM_REPORTS = 'view_team_reports',
  VIEW_ALL_REPORTS = 'view_all_reports',
  EXPORT_REPORTS = 'export_reports',
  VIEW_ANALYTICS = 'view_analytics',
  VIEW_ADVANCED_ANALYTICS = 'view_advanced_analytics',

  // System Administration
  VIEW_SYSTEM_SETTINGS = 'view_system_settings',
  EDIT_SYSTEM_SETTINGS = 'edit_system_settings',
  VIEW_AUDIT_LOG = 'view_audit_log',
  MANAGE_INTEGRATIONS = 'manage_integrations',
  SYSTEM_BACKUP = 'system_backup',

  // Platform Administration (SUPER_ADMIN only)
  MANAGE_TENANTS = 'manage_tenants',
  PLATFORM_ANALYTICS = 'platform_analytics',
  PLATFORM_BILLING = 'platform_billing',
  PLATFORM_MONITORING = 'platform_monitoring',
  MANAGE_PLATFORM_SETTINGS = 'manage_platform_settings'
}

// Permission mapping by role
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.EMPLOYEE]: [
    // Basic self-service permissions
    Permission.VIEW_OWN_ATTENDANCE,
    Permission.VIEW_OWN_SCHEDULE,
    Permission.VIEW_OWN_TASKS,
    Permission.VIEW_OWN_REPORTS
  ],

  [UserRole.SUPERVISOR]: [
    // All employee permissions plus team management
    Permission.VIEW_OWN_ATTENDANCE,
    Permission.VIEW_OWN_SCHEDULE,
    Permission.VIEW_OWN_TASKS,
    Permission.VIEW_OWN_REPORTS,
    Permission.VIEW_TEAM_ATTENDANCE,
    Permission.VIEW_TEAM_SCHEDULE,
    Permission.VIEW_TEAM_TASKS,
    Permission.VIEW_TEAM_MEMBERS,
    Permission.VIEW_TEAM_REPORTS,
    Permission.CREATE_TASK,
    Permission.ASSIGN_TASK,
    Permission.EDIT_TASK
  ],

  [UserRole.MANAGER]: [
    // All supervisor permissions plus broader management
    Permission.VIEW_OWN_ATTENDANCE,
    Permission.VIEW_OWN_SCHEDULE,
    Permission.VIEW_OWN_TASKS,
    Permission.VIEW_OWN_REPORTS,
    Permission.VIEW_TEAM_ATTENDANCE,
    Permission.VIEW_TEAM_SCHEDULE,
    Permission.VIEW_TEAM_TASKS,
    Permission.VIEW_TEAM_MEMBERS,
    Permission.VIEW_TEAM_REPORTS,
    Permission.CREATE_TASK,
    Permission.ASSIGN_TASK,
    Permission.EDIT_TASK,
    Permission.VIEW_ALL_ATTENDANCE,
    Permission.VIEW_ALL_SCHEDULES,
    Permission.VIEW_ALL_TASKS,
    Permission.VIEW_ALL_USERS,
    Permission.VIEW_ALL_REPORTS,
    Permission.CREATE_SCHEDULE,
    Permission.EDIT_SCHEDULE,
    Permission.APPROVE_SCHEDULE_CHANGES,
    Permission.EDIT_ATTENDANCE,
    Permission.DELETE_TASK,
    Permission.CREATE_USER,
    Permission.EDIT_USER,
    Permission.VIEW_ORGANIZATION,
    Permission.EXPORT_REPORTS,
    Permission.VIEW_ANALYTICS,
    // Per NAVIGATION-FLOW-MOCKUP.html line 722-726: Manager can view settings
    Permission.VIEW_SYSTEM_SETTINGS
  ],

  [UserRole.ADMIN]: [
    // All tenant-level permissions (excludes platform-level permissions)
    ...Object.values(Permission).filter(p => ![
      Permission.MANAGE_TENANTS,
      Permission.PLATFORM_ANALYTICS, 
      Permission.PLATFORM_BILLING,
      Permission.PLATFORM_MONITORING,
      Permission.MANAGE_PLATFORM_SETTINGS
    ].includes(p))
  ],

  [UserRole.SUPER_ADMIN]: [
    // All permissions including platform-level
    ...Object.values(Permission)
  ]
};

// Role hierarchy levels for comparison
const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRole.EMPLOYEE]: 1,
  [UserRole.SUPERVISOR]: 2,
  [UserRole.MANAGER]: 3,
  [UserRole.ADMIN]: 4,
  [UserRole.SUPER_ADMIN]: 5  // Platform-level administrator
};

/**
 * Check if a user has a specific permission
 */
export const hasPermission = (userRole?: string, permission?: Permission): boolean => {
  if (!userRole || !permission) return false;
  
  const role = userRole as UserRole;
  const permissions = ROLE_PERMISSIONS[role] || [];
  
  return permissions.includes(permission);
};

/**
 * Check if a user has any of the specified permissions
 */
export const hasAnyPermission = (userRole?: string, permissions?: Permission[]): boolean => {
  if (!userRole || !permissions || permissions.length === 0) return false;
  
  return permissions.some(permission => hasPermission(userRole, permission));
};

/**
 * Check if a user has all of the specified permissions
 */
export const hasAllPermissions = (userRole?: string, permissions?: Permission[]): boolean => {
  if (!userRole || !permissions || permissions.length === 0) return false;
  
  return permissions.every(permission => hasPermission(userRole, permission));
};

/**
 * Check if user role is at least the specified minimum role
 */
export const hasMinimumRole = (userRole?: string, minimumRole?: UserRole): boolean => {
  if (!userRole || !minimumRole) return false;
  
  const currentLevel = ROLE_HIERARCHY[userRole as UserRole] || 0;
  const minimumLevel = ROLE_HIERARCHY[minimumRole] || 0;
  
  return currentLevel >= minimumLevel;
};

/**
 * Get all permissions for a role
 */
export const getPermissionsForRole = (role?: string): Permission[] => {
  if (!role) return [];
  return ROLE_PERMISSIONS[role as UserRole] || [];
};

/**
 * Check if user can access a specific feature/page
 */
export const canAccessFeature = (userRole?: string, feature?: string): boolean => {
  if (!userRole || !feature) return false;

  // Feature access mapping
  const featurePermissions: Record<string, Permission[]> = {
    // Dashboard
    'dashboard': [Permission.VIEW_OWN_ATTENDANCE],
    'analytics': [Permission.VIEW_ANALYTICS],
    'advanced_analytics': [Permission.VIEW_ADVANCED_ANALYTICS],

    // Attendance
    'attendance': [Permission.VIEW_OWN_ATTENDANCE],
    'team_attendance': [Permission.VIEW_TEAM_ATTENDANCE],
    'all_attendance': [Permission.VIEW_ALL_ATTENDANCE],

    // Schedule
    'schedule': [Permission.VIEW_OWN_SCHEDULE],
    'team_schedule': [Permission.VIEW_TEAM_SCHEDULE],
    'schedule_management': [Permission.CREATE_SCHEDULE, Permission.EDIT_SCHEDULE],

    // Tasks
    'tasks': [Permission.VIEW_OWN_TASKS],
    'team_tasks': [Permission.VIEW_TEAM_TASKS],
    'task_management': [Permission.CREATE_TASK, Permission.ASSIGN_TASK],

    // Users
    'users': [Permission.VIEW_TEAM_MEMBERS],
    'user_management': [Permission.CREATE_USER, Permission.EDIT_USER],

    // Organization
    'organizations': [Permission.VIEW_ORGANIZATION],
    'organization_management': [Permission.EDIT_ORGANIZATION, Permission.MANAGE_BRANCHES],

    // Reports
    'reports': [Permission.VIEW_OWN_REPORTS],
    'team_reports': [Permission.VIEW_TEAM_REPORTS],
    'export_reports': [Permission.EXPORT_REPORTS],

    // DOC-GAP-003: Split Shift feature (PM/PJM Decision Jan 6, 2026)
    // Manager+ only - view split shift summary and allowance calculations
    'split-shift': [Permission.VIEW_ALL_SCHEDULES],

    // Settings & Admin
    'settings': [Permission.VIEW_SYSTEM_SETTINGS],
    'admin': [Permission.EDIT_SYSTEM_SETTINGS],
    'audit_log': [Permission.VIEW_AUDIT_LOG]
  };

  const requiredPermissions = featurePermissions[feature];
  if (!requiredPermissions || requiredPermissions.length === 0) {
    // If no specific permissions defined, allow access
    return true;
  }

  // User needs at least one of the required permissions
  return hasAnyPermission(userRole, requiredPermissions);
};

/**
 * Get user's role display name in Vietnamese
 */
export const getRoleDisplayName = (role?: string): string => {
  const roleNames: Record<string, string> = {
    [UserRole.EMPLOYEE]: 'Nhân viên',
    [UserRole.SUPERVISOR]: 'Giám sát',
    [UserRole.MANAGER]: 'Quản lý',
    [UserRole.ADMIN]: 'Quản trị viên',
    [UserRole.SUPER_ADMIN]: 'Quản trị hệ thống'
  };
  
  return roleNames[role || ''] || 'Không xác định';
};

/**
 * Get role badge color for UI display
 */
export const getRoleBadgeColor = (role?: string): string => {
  const roleColors: Record<string, string> = {
    [UserRole.EMPLOYEE]: 'bg-blue-100 text-blue-700',
    [UserRole.SUPERVISOR]: 'bg-green-100 text-green-700',
    [UserRole.MANAGER]: 'bg-purple-100 text-purple-700',
    [UserRole.ADMIN]: 'bg-red-100 text-red-700',
    [UserRole.SUPER_ADMIN]: 'bg-gradient-to-r from-purple-600 to-red-600 text-white font-bold'
  };
  
  return roleColors[role || ''] || 'bg-gray-100 text-gray-700';
};

/**
 * Filter navigation items based on user permissions
 */
export const filterNavigationByPermissions = (
  items: Array<{ requiredPermission?: Permission; requiredFeature?: string }>, 
  userRole?: string
): Array<{ requiredPermission?: Permission; requiredFeature?: string }> => {
  return items.filter(item => {
    if (item.requiredPermission) {
      return hasPermission(userRole, item.requiredPermission);
    }
    
    if (item.requiredFeature) {
      return canAccessFeature(userRole, item.requiredFeature);
    }
    
    // If no requirements specified, allow access
    return true;
  });
};